/**
 * The marketer's side of the demo — a compact mock of Sked (SKE-4166, phase 2).
 *
 * Purely presentational: every value it renders comes from `useDemoMirror`,
 * which derives it from the visitor's demo store. It issues no request, owns no
 * copy of the posts, and never mutates anything — so it cannot drift from the
 * approver UI the visitor is driving.
 *
 * Tailwind only (styled-components are lint-blocked in this app). All motion is
 * behind `motion-safe:`, so under `prefers-reduced-motion: reduce` the panel
 * still updates — it just snaps rather than slides.
 */

import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import { format, formatDistanceToNowStrict } from 'date-fns'

import ImageWithFallback from 'Components/Common/ImageWithFallback'

import type { MirrorNotice, MirrorPost, MirrorProgress } from './demoMirror'
import { demoFormat, demoStrings } from './demoStrings'
import type { DemoStatusType } from './demoTypes'

/**
 * Stagger the load-in. `animate-in` renders the delay through
 * `--tw-animation-delay` (it builds an `animation` shorthand, which would win
 * over an inline `animation-delay`), so the variable is what we set.
 */
const staggerStyle = (index: number): React.CSSProperties =>
  ({ '--tw-animation-delay': `${index * 45}ms` }) as React.CSSProperties

const STATUS_CHIP_CLASS: Record<DemoStatusType, string> = {
  pending: 'bg-yellow-50 text-yellow-800 ring-yellow-200',
  approved: 'bg-green-50 text-green-700 ring-green-200',
  requestChange: 'bg-red-50 text-red-600 ring-red-200',
}

export const STATUS_DOT_CLASS: Record<DemoStatusType, string> = {
  pending: 'bg-yellow-400',
  approved: 'bg-green-500',
  requestChange: 'bg-red-500',
}

const STATUS_RAIL_CLASS: Record<DemoStatusType, string> = {
  pending: 'bg-neutral-100',
  approved: 'bg-green-400',
  requestChange: 'bg-red-400',
}

const formatWhen = (when: string | undefined): string => {
  if (!when) {
    return ''
  }
  const date = new Date(when)
  return Number.isNaN(date.getTime()) ? '' : format(date, 'EEE d MMM · h:mmaaa')
}

const formatAgo = (at: string): string => {
  const date = new Date(at)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  // Anything inside the first minute reads better as "just now" than as
  // "0 seconds ago", and the demo's actions are all seconds old.
  return Date.now() - date.getTime() < 60_000
    ? demoStrings.mirrorJustNow
    : formatDistanceToNowStrict(date, { addSuffix: true })
}

/* -------------------------------------------------------------------------- */
/* Pieces                                                                      */
/* -------------------------------------------------------------------------- */

export const StatusChip: React.FC<{
  status: DemoStatusType
  label: string
}> = ({ status, label }) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
      STATUS_CHIP_CLASS[status]
    )}
  >
    <span
      aria-hidden="true"
      className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT_CLASS[status])}
    />
    {label}
  </span>
)

const BrandAvatar: React.FC<{ post: MirrorPost }> = ({ post }) =>
  post.avatar ? (
    <ImageWithFallback
      variant="avatar"
      fallbackLabel={post.brand}
      src={post.avatar}
      alt=""
      className="h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-neutral-200"
      fallbackClassName="h-7 w-7 shrink-0 rounded-full bg-blue-50 text-blue-600 ring-1 ring-neutral-200"
    />
  ) : (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-600"
    >
      {post.brand.slice(0, 1).toUpperCase()}
    </span>
  )

const MirrorRow: React.FC<{
  post: MirrorPost
  index: number
  isFlashed: boolean
}> = ({ post, index, isFlashed }) => (
  <li
    data-mirror-postid={post.id}
    style={staggerStyle(index)}
    className={cn(
      'relative flex gap-3 overflow-hidden rounded-lg border bg-white p-3 pl-4',
      'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:fill-mode-both motion-safe:duration-300',
      isFlashed
        ? 'border-blue-200 shadow-[0_0_0_3px_var(--color-blue-37)]'
        : 'border-neutral-100'
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        'absolute inset-y-0 left-0 w-1',
        STATUS_RAIL_CLASS[post.status]
      )}
    />

    {post.thumbnail ? (
      <ImageWithFallback
        variant="media"
        src={post.thumbnail}
        alt=""
        className="h-12 w-12 shrink-0 rounded-md object-cover"
        fallbackClassName="h-12 w-12 shrink-0 rounded-md"
      />
    ) : (
      <span
        aria-hidden="true"
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-neutral-50 text-neutral-300"
      >
        <i className="fa-regular fa-image" />
      </span>
    )}

    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <BrandAvatar post={post} />
        <span className="truncate text-sm font-semibold text-neutral-800">
          {post.brand}
        </span>
      </div>
      <p className="mt-1 truncate text-xs text-neutral-500">
        {formatWhen(post.when)}
      </p>
      <p className="mt-1 line-clamp-2 text-xs text-neutral-600">
        {post.snippet}
      </p>
      <div className="mt-2">
        <StatusChip status={post.status} label={post.statusLabel} />
      </div>
    </div>
  </li>
)

const NoticeToast: React.FC<{ notice: MirrorNotice }> = ({ notice }) => (
  <div
    role="status"
    className={cn(
      'flex items-start gap-2 rounded-lg bg-neutral-900/95 px-3 py-2 text-xs text-white shadow-lg backdrop-blur',
      'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-300'
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        'mt-1 h-1.5 w-1.5 shrink-0 rounded-full',
        STATUS_DOT_CLASS[notice.status]
      )}
    />
    <span className="leading-snug">{notice.message}</span>
  </div>
)

/* -------------------------------------------------------------------------- */
/* Panel                                                                       */
/* -------------------------------------------------------------------------- */

export interface MarketerMirrorProps {
  posts: MirrorPost[]
  progress: MirrorProgress
  activity: MirrorNotice[]
  notices: MirrorNotice[]
  flashedPostIds: Set<string>
  /**
   * The workspace shown in the mock Sked chrome. Defaults to the demo's own
   * fictional agency; becomes the visitor's client once they personalize the
   * board (SKE-4166 phase 4).
   */
  workspaceName?: string
  /** Rendered in the panel header — the rail's collapse control, say. */
  headerAction?: React.ReactNode
  className?: string
}

const MarketerMirror: React.FC<MarketerMirrorProps> = ({
  posts,
  progress,
  activity,
  notices,
  flashedPostIds,
  workspaceName = demoStrings.mirrorWorkspaceName,
  headerAction,
  className,
}) => {
  const reviewedPercent =
    progress.total === 0
      ? 0
      : Math.round((progress.reviewed / progress.total) * 100)

  return (
    <section
      aria-label={demoStrings.mirrorAriaLabel}
      className={cn(
        'relative flex h-full flex-col overflow-hidden bg-neutral-25',
        className
      )}
    >
      {/* Mock Sked chrome — signals "this is the other side of the product"
          without pretending to be a full second app. */}
      <header className="shrink-0 bg-blue-800 px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500 font-gotham text-sm font-extrabold"
          >
            S
          </span>
          <span className="font-gotham text-sm font-bold tracking-tight">
            Sked
          </span>
          <span aria-hidden="true" className="text-blue-300">
            /
          </span>
          <span className="truncate text-sm text-blue-100">
            {workspaceName}
          </span>
          <span className="ml-auto shrink-0 rounded-full bg-blue-700/60 px-2 py-0.5 text-[11px] text-blue-100">
            {demoStrings.mirrorWorkspaceMeta}
          </span>
        </div>
      </header>

      <div className="shrink-0 border-b border-neutral-100 bg-white px-4 py-3">
        <div className="flex items-start gap-2">
          <div className="min-w-0">
            <h2 className="font-gotham text-base font-extrabold text-neutral-800">
              {demoStrings.mirrorPanelTitle}
            </h2>
            <p className="mt-0.5 text-xs text-neutral-500">
              {demoStrings.mirrorPanelSubtitle}
            </p>
          </div>
          {headerAction ? (
            <div className="ml-auto shrink-0">{headerAction}</div>
          ) : null}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs font-medium text-neutral-600">
          <span>
            {demoFormat('mirrorProgressLabel', {
              done: progress.reviewed,
              total: progress.total,
            })}
          </span>
          <span className={cn(progress.pending === 0 && 'text-green-600')}>
            {progress.pending === 0
              ? demoStrings.mirrorAllClearLabel
              : demoFormat('mirrorAwaitingLabel', { count: progress.pending })}
          </span>
        </div>

        <div
          className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100"
          role="progressbar"
          aria-valuenow={reviewedPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={demoFormat('mirrorProgressLabel', {
            done: progress.reviewed,
            total: progress.total,
          })}
        >
          {/* At 100% the fill flips to the approval green and lights up — the
              milestone flourish that pairs with the client-side confetti. It's
              a static state (colour + glow), so it stands under reduced motion;
              only the width fill animates, and that's already `motion-safe:`. */}
          <span
            className={cn(
              'block h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500 motion-safe:ease-out',
              reviewedPercent === 100
                ? 'bg-green-500 shadow-[0_0_10px_1px_rgba(34,197,94,0.75)]'
                : 'bg-blue-500'
            )}
            style={{ width: `${reviewedPercent}%` }}
          />
        </div>
      </div>

      {/* Toasts sit over the list so the eye lands on the message first, then
          on the row that just changed underneath it. */}
      {notices.length > 0 && (
        <div className="pointer-events-none absolute inset-x-3 top-[76px] z-20 flex flex-col gap-2">
          {notices.map((notice) => (
            <NoticeToast key={notice.key} notice={notice} />
          ))}
        </div>
      )}

      <ol className="flex-1 space-y-2 overflow-y-auto p-3">
        {posts.length === 0 ? (
          <li className="rounded-lg border border-dashed border-neutral-200 p-6 text-center text-xs text-neutral-400">
            {demoStrings.mirrorEmpty}
          </li>
        ) : (
          posts.map((post, index) => (
            <MirrorRow
              // Keying on the STATUS as well as the id remounts the row when it
              // flips, which replays the enter animation — that is the
              // status-flip transition, with no timer and no animation state to
              // keep in sync with the store.
              key={`${post.id}:${post.status}`}
              post={post}
              index={index}
              isFlashed={flashedPostIds.has(post.id)}
            />
          ))
        )}
      </ol>

      <div className="shrink-0 border-t border-neutral-100 bg-white px-4 py-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
          {demoStrings.mirrorActivityTitle}
        </h3>
        {activity.length === 0 ? (
          <p className="mt-2 text-xs text-neutral-400">
            {demoStrings.mirrorActivityEmpty}
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {activity.map((notice) => (
              <li
                key={notice.key}
                className="flex items-start gap-2 text-xs text-neutral-600 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                    STATUS_DOT_CLASS[notice.status]
                  )}
                />
                <span className="min-w-0 flex-1 leading-snug">
                  {notice.message}
                </span>
                <span className="shrink-0 text-neutral-400">
                  {formatAgo(notice.at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

export default MarketerMirror
